// PHASE_1 / B3 — the org-chart Content component (ADR-014 §2).
// Faz 8.6 / Batch A5 restyles the VISUAL layer to the novem org-chart language
// (NovemHR-ekranlar org.jsx OrgSchemaPanel — serif PageHeader+em, a summary
// StatCard strip, the tree in a Card, a kind legend). The DATA layer is
// untouched: the SAME one-shot useQuery over GET /v1/org/chart; the server
// scopes what's visible (admin /org vs read-only /me/org). The chart itself is
// the VIRTUALIZED <OrgChart> (the phase exit gate) — only its row markup moved
// to the novem visual language; the windowing logic is unchanged.
//
// Pure, shell-agnostic Content. The menu/role is UX only (ADR-008/ADR-017 — the
// frontend is never a trust boundary). Solid-line tree only (ADR-012). The
// summary counts are integers derived from the loaded node set, never money.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Network, Sigma, UserRoundCog } from "lucide-react";

import { Card, ErrorState, Loading, StatCard } from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { fetchOrgChart, type OrgChartResponse } from "./api";
import { OrgChart } from "./OrgChart";

export function OrgChartContent(): React.ReactElement {
  const { t } = useTranslation();

  // The as-of-date picker is intentionally NOT rendered: the backend resolves
  // an as-of snapshot id but STILL serves the LIVE node list (point-in-time
  // snapshot capture is deferred). Shipping a date control the server ignores
  // would be a misleading no-op, so B3 always shows the live chart with an
  // explicit note. [P1/B3] carryover: snapshot-graph fetch + a real historical
  // (as-of) view once the daily capture job lands.
  const query = useQuery<OrgChartResponse>({
    queryKey: ["org", "chart", "live"],
    queryFn: () => fetchOrgChart(),
  });

  const nodes = query.data?.nodes ?? [];

  // Summary counts for the novem StatCard strip (org.jsx Özet bar) — derived
  // from the loaded solid-line set: managers = distinct managerIds referenced;
  // top-level = nodes with no in-set manager (the chart roots).
  const summary = useMemo(() => {
    const ids = new Set(nodes.map((n) => n.employeeId));
    const managerIds = new Set<string>();
    let topLevel = 0;
    for (const n of nodes) {
      if (n.managerId !== null && ids.has(n.managerId)) managerIds.add(n.managerId);
      else topLevel += 1;
    }
    return { people: nodes.length, managers: managerIds.size, topLevel };
  }, [nodes]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {query.isLoading ? (
        <Loading label={t("org.loading")} rows={6} />
      ) : query.isError ? (
        <ErrorState
          title={t("org.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              label={t("org.kpi_people")}
              value={String(summary.people)}
              tone="accent"
              icon={<Sigma size={18} />}
            />
            <StatCard
              label={t("org.kpi_managers")}
              value={String(summary.managers)}
              tone="info"
              icon={<UserRoundCog size={18} />}
            />
            <StatCard
              label={t("org.kpi_top_level")}
              value={String(summary.topLevel)}
              tone="success"
              icon={<Network size={18} />}
            />
          </div>

          <p className="font-serif text-sm italic text-[var(--fg-muted)]">
            {t("org.chart_live_note")}
          </p>

          <Card className="overflow-hidden">
            <OrgChart
              nodes={nodes}
              labels={{
                regionLabel: t("org.chart_region"),
                expandLabel: t("org.chart_expand"),
                collapseLabel: t("org.chart_collapse"),
                noTitle: t("org.chart_no_title"),
                empty: t("org.chart_empty"),
                reportsLabel: (count): string => t("org.chart_reports", { count }),
              }}
            />
          </Card>

          {/* Kind legend (org.jsx tree footer) — the node/leaf dot key. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-1">
            <LegendDot
              color="var(--accent)"
              label={t("org.chart_legend_node")}
            />
            <LegendDot
              color="var(--border-default)"
              label={t("org.chart_legend_leaf")}
            />
            <span className="font-sans text-[12px] text-[var(--fg-muted)]">
              {t("org.chart_node_count", { count: summary.people })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function LegendDot({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}): React.ReactElement {
  return (
    <span className="flex items-center gap-2 font-sans text-[12px] text-[var(--fg-muted)]">
      <span
        aria-hidden="true"
        className="h-[9px] w-[9px] rounded-[3px]"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}
